import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import Step1RegistrationPanel from '../RegistrationStep1Page/Panels/Step1RegistrationPanel/Step1RegistrationPanel';
import UAMModal from '../../components/UAMModal/UAMModal';
import { makeInitialField, setFieldAndValidate } from '../../utils/field-utils';
import { userFieldSchemas } from '../../utils/validation-utils';
import { getIdTokenIfValid } from '../../utils/jwt-utils';
import { getReturnUrlByDestination } from '../../../shared-with-server/destination-utils';
import { destinationInfo, instructorRegistrationDefaultSource, instructorRegistrationPermittedSources } from '../../destructured-front-end-config';
import { InstructorVerificationContentContainer } from './InstructorVerificationPageStyles';
import { PrsDefaultButton } from '../../styles-and-themes/styles/prsButtons';

/**
 * Page for instructor verification flow.
 * This repurposes the step-1 registration page with modifications:
 * - Hides country field
 * - Disables all fields except Role and Discipline
 * - Uses Cancel/Submit buttons instead of Back/Continue
 * - Validates source parameter from querystring
 * - Requires user to be signed in
 */
const InstructorVerificationPage = ({ brand, destPath, queryStringData }) => {
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showInstructorExistsModal, setShowInstructorExistsModal] = useState(false);
  const [firstName, setFirstName] = useState(makeInitialField(''));
  const [lastName, setLastName] = useState(makeInitialField(''));
  const [countryOrRegion, setCountryOrRegion] = useState(makeInitialField(''));
  const [role, setRole] = useState(makeInitialField(''));
  const [roleType, setRoleType] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [school, setSchool] = useState(makeInitialField(''));
  const [discipline, setDiscipline] = useState(makeInitialField(''));
  const [availableDisciplines, setAvailableDisciplines] = useState([]);

  // Get and validate source parameter
  const getValidatedSource = () => {
    // Support getting "source" query parameter in any casing (e.g., "SOURCE", "Source", etc.)
    const sourceKey = Object.keys(queryStringData || {}).find(
      (key) => key.toLowerCase() === 'source'
    );
    const sourceLower = queryStringData?.[sourceKey]?.toLowerCase()?.trim();

    // If no source provided or empty, use default
    if (!sourceLower) {
      return instructorRegistrationDefaultSource || '';
    }

    // Check if source is in permitted list (case-insensitive)
    const matchedSource = instructorRegistrationPermittedSources
      .find(
        (permittedSource) => permittedSource.toLowerCase() === sourceLower
      );

    return matchedSource?.length
      ? matchedSource // return case from permitted list
      : (instructorRegistrationDefaultSource || ''); // default if not in permitted list
  };

  const validatedSource = getValidatedSource();

  // Load user data and roles/disciplines
  useEffect(() => {
    const idToken = getIdTokenIfValid('id_token');

    // Check if user is signed in
    if (!idToken || !idToken.user_id) {
      setErrorMessage('You must be signed in to proceed');
      setIsLoading(false);
      return;
    }

    setUserId(idToken.user_id);

    // Set initial message about verification time
    setErrorMessage('Verification may take up to 48 hours.');

    // Fetch user profile data to populate disabled fields
    axios.get(`/iam/user/${idToken.user_id}`)
      .then(({ data }) => {
        if (data) {
          // Populate fields from user data
          if (data.FirstName) {
            setFieldAndValidate(firstName, setFirstName, data.FirstName, userFieldSchemas.firstName);
          }
          if (data.LastName) {
            setFieldAndValidate(lastName, setLastName, data.LastName, userFieldSchemas.lastName);
          }
          if (data.School_or_Institution__c) {
            setFieldAndValidate(
              school,
              setSchool,
              {
                institution: data.School_or_Institution__c,
                institutionId: data.InstitutionId__c || null,
                cAccount: data.C_Acct__c || null,
              },
              userFieldSchemas.school,
            );
          }
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading user data', err);
        setErrorMessage('An error occurred while loading user data. Please try again.');
        setIsLoading(false);
      });

    // Fetch available roles (exclude Student role for instructor verification)
    axios.get('/api/v2/fi/user-roles').then((res) => {
      const rolesWithoutStudent = res.data.filter((resRole) => resRole.type !== 'Student');
      setAvailableRoles(rolesWithoutStudent);
    });

    // Fetch availables disciplines
    axios.get('/api/v2/fi/instructor-disciplines').then((res) => {
      setAvailableDisciplines(res.data);
    });

    // Check if instructor status already exists in user metadata
    axios.get('/api/v2/fi/token').then((res) => {
      if (res.data?.userMetadata?.instructorStatus) {
        setShowInstructorExistsModal(true);
      }
    }).catch((err) => {
      // Silently fail - if we can't get token info, we'll just proceed normally
      console.error('Error checking token for instructor status', err);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const returnUrl = getReturnUrlByDestination(destinationInfo, destPath?.dest, destPath?.path) || destPath?.retURL || queryStringData?.retURL;
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      window.location.href = '/';
    }
  };

  const handleInstructorExistsModalOk = () => {
    const returnUrl = getReturnUrlByDestination(destinationInfo, destPath?.dest, destPath?.path) || destPath?.retURL || queryStringData?.retURL;
    if (returnUrl) {
      window.location.href = returnUrl;
    } else {
      window.location.href = '/';
    }
  };

  const handleSubmit = (e, roleValue, disciplineValue) => {
    e.preventDefault();
    e.stopPropagation();

    if (!userId) {
      setErrorMessage('User ID not found. Please try again.');
      return;
    }

    // Clear any previous error
    setErrorMessage(null);

    axios.post(
      `/api/v2/fi/users/${userId}/instructor-verification`,
      {
        role: roleValue,
        discipline: disciplineValue,
        source: validatedSource,
      },
      { validateStatus: (status) => status < 500 },
    ).then((result) => {
      if (result.status === 200 || result.status === 201) {
        // Success - redirect to retURL
        const returnUrl = getReturnUrlByDestination(destinationInfo, destPath?.dest, destPath?.path) || destPath?.retURL || queryStringData?.retURL;
        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          window.location.href = '/';
        }
      } else if (result.status === 409 && result.data?.message === 'Instructor status already exists for this user') {
        setShowInstructorExistsModal(true);
      } else {
        // Generic error for other status codes (like 403)
        setErrorMessage('An error occurred while submitting instructor verification. Please try again.');
        console.error('Error submitting instructor verification', result);
      }
    }).catch((error) => {
      setErrorMessage('An error occurred while submitting instructor verification. Please try again.');
      console.error('Error submitting instructor verification', error);
    });
  };

  // Handle scroll lock for Prose modal
  useEffect(() => {
    if (showInstructorExistsModal) {
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.scrollbarGutter = 'stable';
    } else {
      document.documentElement.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
    };
  }, [showInstructorExistsModal]);

  // Show error if not signed in
  if (errorMessage && !userId && !isLoading) {
    return (
      <BasePageUAM activeTab={2} brand={brand} destPath={destPath} needsDestPath>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p style={{ color: '#E0205D', fontSize: '1.125rem' }}>{errorMessage}</p>
        </div>
      </BasePageUAM>
    );
  }

  return (
    <BasePageUAM activeTab={2} brand={brand} destPath={destPath} needsDestPath showTabs={false}>
      {
        showInstructorExistsModal && (
          <UAMModal
            initialFocus="#instructorExistsOk"
            showCloseNav={false}
            suppressHeader
            cancelDeactivatesModal={false}
            footerContent={(
              <PrsDefaultButton id="instructorExistsOk" onClick={handleInstructorExistsModalOk}>
                Ok
              </PrsDefaultButton>
            )}
          >
            <InstructorVerificationContentContainer>
              <p>
                <span>Your instructor verification request has already been received.</span>
                <span>Verification may take up to 48 hours.</span>
              </p>
            </InstructorVerificationContentContainer>
          </UAMModal>
        )
      }
      {
        isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        ) : (
          <Step1RegistrationPanel
            brand={brand}
            destPath={destPath}
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            countryOrRegion={countryOrRegion}
            setCountryOrRegion={setCountryOrRegion}
            role={role}
            setRole={setRole}
            availableRoles={availableRoles}
            roleType={roleType}
            setRoleType={setRoleType}
            school={school}
            setSchool={setSchool}
            discipline={discipline}
            setDiscipline={setDiscipline}
            availableDisciplines={availableDisciplines}
            setEmailPending={() => {}}
            setRegistrationComplete={() => {}}
            hideRoleSelector={false}
            disabledFields={{
              firstName: true,
              lastName: true,
              countryOrRegion: true,
              school: true,
            }}
            hideFields={{
              firstName: false,
              lastName: false,
              countryOrRegion: true, // Hide country field
              school: false,
            }}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            errorMessage={errorMessage}
            heading={(
              <h2 style={{
                fontFamily: '"Figtree", ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
                fontSize: '1.125rem',
                fontWeight: 600,
                marginBottom: '1.5rem',
                width: '100%',
              }}
              >
                Instructor Verification
              </h2>
            )}
          />
        )
      }
    </BasePageUAM>
  );
};

InstructorVerificationPage.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
  queryStringData: PropTypes.object,
};

InstructorVerificationPage.defaultProps = {
  queryStringData: {},
};

export default InstructorVerificationPage;
